import React from 'react';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  fullNameSchema,
  fullNameUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { formatFullName } from '../../../utils/helpers';
import content from '../../../utils/content.json';

function checkIsItemIncomplete(item) {
  return !item?.otherServiceName?.first || !item?.otherServiceName?.last;
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'veteranPreviousNames',
  nounSingular: content['military-service-names-noun-singular'],
  nounPlural: content['military-service-names-noun-plural'],
  required: false,
  isItemIncomplete: item => checkIsItemIncomplete(item),
  maxItems: 2,
  text: {
    summaryTitle: 'Review the Veteran’s other service names',
    alertMaxItems: (
      <div>
        <p className="vads-u-margin-top--0">
          You have added the maximum number of allowed service names for this
          application. Additional service names can be added using a Statement
          in Support of Claim (VA Form 21-4138).
        </p>
        <p>
          You can upload this document at the end of this application. Or you
          can send it to us by mail.
        </p>
        <va-link
          href="https://www.va.gov/find-forms/about-form-21-4138/"
          external
          text="Get VA Form 21-4138 to download"
        />
      </div>
    ),
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      'service name?',
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      'service name?',
    ),
    cancelAddDescription:
      content['military-service-names-cancel-add-description'],
    cancelAddReviewDescription:
      content['military-service-names-cancel-add-description'],
    cancelEditDescription:
      content['military-service-names-cancel-edit-description'],
    cancelEditReviewDescription:
      content['military-service-names-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: props => {
      const serviceName = props?.itemData?.otherServiceName;
      const name = serviceName
        ? formatFullName(serviceName)
        : content['military-service-names-delete-default-name'];
      return replaceStrValues(
        content['military-service-names-delete-description'],
        name,
      );
    },
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      'service name',
    ),
    deleteYes: content['array-delete-yes'],
    getItemName: item => {
      const name = item?.otherServiceName;
      if (!name?.first && !name?.last) return '';

      return formatFullName(name);
    },
  },
};

function introDescription() {
  return (
    <div>
      <p>{content['military-service-names-intro-description']}</p>
    </div>
  );
}

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['military-service-names-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:hasOtherServiceNames': arrayBuilderYesNoUI(
      options,
      {
        title: content['military-service-names-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title: content['military-service-names-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
  },
  schema: {
    type: 'object',
    required: ['view:hasOtherServiceNames'],
    properties: {
      'view:hasOtherServiceNames': arrayBuilderYesNoSchema,
    },
  },
};

/** @returns {PageSchema} */
const otherServiceNamePage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['military-service-names-other-name-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    otherServiceName: fullNameUI(),
  },
  schema: {
    type: 'object',
    properties: {
      otherServiceName: fullNameSchema,
    },
    required: ['otherServiceName'],
  },
};

export const otherServiceNamesPages = arrayBuilderPages(
  options,
  pageBuilder => ({
    otherServiceNamesIntro: pageBuilder.introPage({
      title: content['military-service-names-title'],
      path: 'veteran/other-service-names-intro',
      depends: formData => formData.receivedBenefits === false,
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    otherServiceNamesSummary: pageBuilder.summaryPage({
      title: content['military-service-names-summary-add'],
      path: 'veteran/other-service-names',
      depends: formData => formData.receivedBenefits === false,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    otherServiceNamePage: pageBuilder.itemPage({
      title: content['military-service-names-other-name-title'],
      path: 'veteran/other-service-names/:index',
      depends: formData => formData.receivedBenefits === false,
      uiSchema: otherServiceNamePage.uiSchema,
      schema: otherServiceNamePage.schema,
    }),
  }),
);
